/**
 * I dati dei documenti ufficiali in forma leggibile (contratto n. 1 versione 2.0, art. 7.11; ADR 0171): la visura scrive
 * «BOOSTAR SOCIETA' A RESPONSABILITA' LIMITATA» e «VIA BITRITTO 114/C», la pagina scrive «Boostar S.r.l.» e «Via
 * Bitritto 114/C». La citazione esatta resta a parte: qui si cambia solo la forma, mai il dato. Nessun nome di azienda nel
 * codice: solo come i registri scrivono.
 */

/** Sigle che restano maiuscole anche in una frase. */
const SIGLE = new Set(['REA', 'PEC', 'ATECO', 'IVA', 'CAP', 'SNC', 'SAS', 'SRL', 'SPA', 'SRLS', 'ISO', 'IEC', 'UNI', 'EN', 'CE', 'UE', 'ONLUS', 'ETS', 'SOA', 'ANAC', 'INPS', 'INAIL', 'CCNL', 'PMI', 'SIAE', 'RSPP', 'DVR', 'POS', 'IT', 'BA', 'MI', 'RM', 'NA', 'TO', 'FI', 'BO', 'GE', 'VE', 'PA', 'CT', 'CA', 'TA', 'LE', 'BR', 'FG', 'BT', 'PD', 'VR', 'VI', 'TV', 'BS', 'BG', 'MB', 'CO', 'VA', 'MO', 'RE', 'PR', 'PC', 'RN', 'FC', 'RA', 'FE', 'PU', 'AN', 'MC', 'AP', 'FM', 'PG', 'TR', 'AQ', 'TE', 'PE', 'CH', 'CB', 'IS', 'CE', 'BN', 'AV', 'SA', 'PZ', 'MT', 'CS', 'CZ', 'KR', 'VV', 'RC', 'ME', 'AG', 'CL', 'EN', 'RG', 'SR', 'TP', 'SS', 'NU', 'OR', 'SU', 'AO', 'BI', 'CN', 'AT', 'AL', 'NO', 'VB', 'VC', 'IM', 'SV', 'SP', 'LO', 'CR', 'MN', 'PV', 'SO', 'LC', 'BZ', 'TN', 'UD', 'GO', 'TS', 'PN', 'RO', 'BL', 'LU', 'MS', 'PI', 'LI', 'AR', 'SI', 'GR', 'PO', 'PT', 'VT', 'RI', 'LT', 'FR']);

/** Le forme giuridiche come le scrive il Registro Imprese, con la loro sigla leggibile. */
const FORME: Array<[RegExp, string]> = [
  [/\bsociet[aà]['’]?\s+a\s+responsabilit[aà]['’]?\s+limitata\s+semplificata\b/i, 'S.r.l.s.'],
  [/\bsociet[aà]['’]?\s+a\s+responsabilit[aà]['’]?\s+limitata\b/i, 'S.r.l.'],
  [/\bsociet[aà]['’]?\s+per\s+azioni\b/i, 'S.p.A.'],
  [/\bsociet[aà]['’]?\s+in\s+accomandita\s+semplice\b/i, 'S.a.s.'],
  [/\bsociet[aà]['’]?\s+in\s+accomandita\s+per\s+azioni\b/i, 'S.a.p.a.'],
  [/\bsociet[aà]['’]?\s+in\s+nome\s+collettivo\b/i, 'S.n.c.'],
  [/\bsociet[aà]['’]?\s+cooperativa\s+sociale\b/i, 'Società cooperativa sociale'],
  [/\bsociet[aà]['’]?\s+cooperativa\b/i, 'Società cooperativa'],
  [/\bs\.?\s?r\.?\s?l\.?\s?s\.?(?=\W|$)/i, 'S.r.l.s.'],
  [/\bs\.?\s?r\.?\s?l\.?(?=\W|$)/i, 'S.r.l.'],
  [/\bs\.?\s?p\.?\s?a\.?(?=\W|$)/i, 'S.p.A.'],
  [/\bs\.?\s?a\.?\s?s\.?(?=\W|$)/i, 'S.a.s.'],
  [/\bs\.?\s?n\.?\s?c\.?(?=\W|$)/i, 'S.n.c.'],
];

/** Le vocali accentate che la visura scrive con l'apostrofo: «SOCIETA'» → «società», «E'» → «è». */
const ACCENTI: Record<string, string> = { a: 'à', e: 'è', i: 'ì', o: 'ò', u: 'ù' };
const restoreAccents = (w: string) => w.replace(/([aeiou])['’`´]$/i, (_, v: string) => (v === v.toUpperCase() ? ACCENTI[v.toLowerCase()]!.toUpperCase() : ACCENTI[v]!));

const isUpper = (s: string) => s === s.toUpperCase() && /\p{L}/u.test(s);
/** Le particelle che in una frase restano minuscole («Sant'Agata di Puglia», «Società a responsabilità»). */
const PARTICELLE = new Set(['di', 'del', 'della', 'dei', 'degli', 'delle', 'da', 'dal', 'in', 'a', 'al', 'ed', 'e', 'su', 'sul', 'sulla', 'la', 'le', 'lo', 'il', 'un', 'per', 'con', 'o']);

/** Una parola scritta in maiuscolo diventa leggibile: iniziale maiuscola, sigle intatte, accenti al posto dell'apostrofo. */
function word(w: string): string {
  const bare = w.replace(/[^\p{L}\p{N}]/gu, '');
  if (!bare) return w;
  if (PARTICELLE.has(bare.toLowerCase())) return w.toLowerCase();
  if (SIGLE.has(bare.toUpperCase()) && bare.length <= 5) return w.toUpperCase();
  // Un codice con numeri (114/C, 70124, BA/2) resta com'è.
  if (/\p{N}/u.test(bare)) return w;
  const lower = restoreAccents(w).toLowerCase();
  // La prima lettera di ogni parola, anche dopo l'apostrofo («Sant'Agata»), ma non dopo una particella con l'apostrofo («D'Amico» sì, «dell'Ovo» no).
  return lower.replace(/(^|[\s-])(\p{L})/gu, (_, a: string, b: string) => a + b.toUpperCase()).replace(/^(\p{L}+)['’](\p{L})/u, (_, a: string, b: string) => (PARTICELLE.has(a.toLowerCase()) ? `${a}’${b}` : `${a}'${b.toUpperCase()}`));
}

/**
 * Un testo in maiuscolo (indirizzi, comuni, oggetti sociali) in forma leggibile. Un testo già scritto in minuscolo o misto
 * non si tocca: è già leggibile. Le sigle restano maiuscole, i numeri e i codici intatti.
 */
export function leggibile(s: string | null | undefined): string {
  const t = (s ?? '').trim();
  if (!t || !isUpper(t)) return t;
  // Le parti tra parentesi (la provincia) restano sigle: «(BA)».
  return t
    .split(/(\s+)/)
    .map((p) => (/^\s+$/.test(p) ? p : /^\([A-Z]{2}\)[.,;:]?$/.test(p) ? p : word(p)))
    .join('')
    .replace(/\s+([,;:.])/g, '$1');
}

/** La denominazione come la scrive il Registro in forma leggibile: «BOOSTAR SOCIETA' A RESPONSABILITA' LIMITATA» → «Boostar S.r.l.». */
export function denominazioneLeggibile(s: string | null | undefined): string {
  let t = (s ?? '').trim();
  if (!t) return t;
  let forma: string | null = null;
  for (const [re, sigla] of FORME) {
    if (re.test(t)) {
      forma = sigla;
      t = t.replace(re, ' ').replace(/\s*[-,]\s*$/, '').replace(/\s{2,}/g, ' ').trim();
      break;
    }
  }
  const nome = isUpper(t) ? leggibile(t) : t;
  return [nome, forma].filter(Boolean).join(' ').trim();
}

/** «MODUGNO (BA)» → «Modugno (BA)»; «SANT'AGATA DI PUGLIA» → «Sant'Agata di Puglia». */
export function comuneLeggibile(s: string | null | undefined): string {
  const t = leggibile(s);
  return t.replace(/\b(Di|Del|Della|Dei|Degli|Delle|Sul|Sulla|Al|In|E)\b/g, (m) => m.toLowerCase());
}

/** Una data AAAA-MM-GG come la legge una persona: «20/10/2026». */
export const giorno = (d: string | null | undefined): string | null => (d ? d.slice(0, 10).split('-').reverse().join('/') : null);
